import {useEffect, useState, useRef, useCallback, useMemo} from "react";
import { Box, MenuItem, FormControl, Select, InputLabel } from "@mui/material";
import {green, red} from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { withTranslation } from "react-i18next";
import { isNull, isUndefined, isEqual, cloneDeep, isEmpty, includes, keys } from "lodash";
import { useDispatch } from "react-redux";

import gridUtils from "@plannel/components/aggrid/GridUtils";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import SplitterContainer from "@plannel/components/SplitterContainer";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import debug from "@plannel/utils/debug-util";
import numberUtil from "@plannel/utils/number-util";
import reduxUtil from "@plannel/utils/redux-util";
import dateUtils from "@plannel/utils/date-util";
import postnService from "@plannel/services/data/postn-service";
import dpTrackingReportService from "@plannel/services/dp/dp-tracking-report-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import ToggleButton from "@plannel/components/ToggleButton";
import codeUtil from "@plannel/pages/utils/code-util";
import colorUtil from "@plannel/pages/utils/color-util";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import userService from "@plannel/services/system/user-service";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";


const DpTrackingReport = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const TRACKING_GRID = "TRACKING_GRID";
  const {BAR, AMT} = codeUtil.toggleType;
  const { UPPER_THRESHOLD, LOWER_THRESHOLD } = codeUtil.workbenchAlertObjs;
  const { roles, dpMeasureType, dpMeasure, getCurrencySymbol } = codeUtil;
  const [versionList, setVersionList] = useState(reduxViewState?.versionList ?? []);
  const [measureList, setMeasureList] = useState(reduxViewState?.measureList ?? []);
  const [allMeasureList, setAllMeasureList] = useState(reduxViewState?.allMeasureList ?? []);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [trackingColumnDefs, setTrackingColumnDefs] = useState([]);
  const [trackingDatas, setTrackingDatas] = useState([]);
  const [trackingChartDatas, setTrackingChartDatas] = useState([]);
  const [trackingChartType, setTrackingChartType] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [toggledMeasure, setToggledMeasure] = useState(reduxViewState?.measureType ?? AMT);
  const toggledChart = BAR;
  const [selectList, setSelectList] = useState(reduxViewState?.hrchyList ?? []);
  const selectCd = useRef(reduxViewState?.selectCd ?? "");
  const selectItemCd = useRef(reduxViewState?.selectItemCd ?? "");
  const [defaultHrchyValue, setDefaultHrchyValue] = useState(reduxViewState?.defaultHrchyValue ?? null);
  const [defaultItemHrchyValue, setDefaultItemHrchyValue] = useState(reduxViewState?.defaultItemHrchyValue ?? null);
  const [isToggle, setIsToggle] = useState(false);
  const [filterValue, setFilterValue] = useState({});

  const refData = useRef([]);
  const workbenchThreshold = useRef({});
  const [formatHeader, setFormatHeader] = useState([]);

  const chartName = useRef();
  const trackingRawRef = useRef(null); // 마지막 조회 응답 — 언어 변경 시 재조립용
  const gridRef = useRef();
  const curVersion = useRef(reduxViewState?.curVersion ?? "");
  const curMeasure = useRef(reduxViewState?.curMeasure ?? "");
  const measureType = useRef()
  const isPostn = useRef(reduxViewState?.isPostn ?? false);
  const [isReduxPostn, setIsReduxPostn] = useState(null);
  const currency = useRef("JPY");
  const trackingColor = { background: green[100] };
  const { colorSet, snackBarMessageColor } = colorUtil;
  const userData = JSON.parse(localStorage.getItem("user"));
  const [userRoles, setUserRoles] = useState(reduxViewState?.userRoles ?? []);

  const defaultTrackingGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ viewName, title, gridId: TRACKING_GRID });
    return defaultSettings;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortListByColumn = (type, list, columnName) => {
    if (type === "desc") {
      list.sort((a, b) => {
        if (a[columnName] > b[columnName]) {
          return -1;
        }
        if (a[columnName] < b[columnName]) {
          return 1;
        }
        return 0;
      });

    } else {
      list.sort((a, b) => {
        if (a[columnName] > b[columnName]) {
          return 1;
        }
        if (a[columnName] < b[columnName]) {
          return -1;
        }
        return 0;
      });

    }
  
    return list;
  }

  const addSymbolAmtMeasure = (param) => {
    const symbol = getCurrencySymbol(currency.current) ?? userData?.currencySymbol ?? "";
    if (!param.value || !param.valueFormatted) {
      return '';
    }
    if (toggledMeasure.includes(dpMeasureType.AMT.toLowerCase())) {
      return symbol + param.valueFormatted;
    }
    return param.valueFormatted;
  };

  const getAlertBackground = (targetData, headerName) => {
    const referenceData = refData.current?.[0] ?? null;
    const threshold = workbenchThreshold.current;

    if (targetData?.data?.version === dpMeasure.SA_QTY.name) {
      return false;
    }

    if (referenceData !== null) {
      const upperThreshold = threshold?.[UPPER_THRESHOLD] ?? 0;
      const lowerThreshold = threshold?.[LOWER_THRESHOLD] ?? 0;

      const refVal = referenceData?.[headerName] ?? 0;
      const targetVal = targetData.value;

      if (targetVal) {
        if (upperThreshold) {
          const upperVal = Number(refVal) * (1 + (upperThreshold / 100));
          if (upperVal < targetVal) {
            return true;
          }
        }
        if (lowerThreshold) {
          const lowerVal = Number(refVal) * (1 + (lowerThreshold / 100));
          if (lowerVal > targetVal) {
            return true;
          }
        }
      }

    }
    return false;

  }

  const setTrackingGridHeader = (headers) => {
    const colDefs = [
      {
        headerName: "", field: "version", width: 300,
        tooltipField: "versionRange",
        valueFormatter: (data) => {
          if (isNull(data.value) || isUndefined(data.value)) {
            return "";
          }
          return gridUtils.gridValueL10N(data.value);
        }
      },
    ];
    for (const index of headers) {
      const headerName = index;
      colDefs.push(
        {
          headerName, field: headerName, width: 100, singleClickEdit: false, type: ['rightAligned'],
          valueFormatter: (data) => {
            if (isNull(data.value) || isUndefined(data.value)) {
              return "";
            }
            return numberUtil.formatNumberData(data, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
          },
          cellStyle: params => {
            if (getAlertBackground(params, headerName)) {
              return { backgroundColor: red[100] };
            } else {
              for (const value of Object.keys(chartName?.current)) {
                if (value === params.colDef?.headerName && params.data?.version === chartName.current?.[value] && params?.value) {
                  return trackingColor;
                }
              }
            }
            return null;
          },
          cellClassRules: {
            alertBackground: (params) => getAlertBackground(params, headerName),
          },
          cellRenderer: addSymbolAmtMeasure,
        });
    }
    setTrackingColumnDefs(colDefs);
  }

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: "DP",
      activeFlg: true,
      configCd: "ACCESS_CONTROL"
    };

    planConfigService.getConfigCd(param)
      .then((response) => {
        if (response?.data) {
          setIsReduxPostn(response.data.boolVal)
        } else {
          setIsReduxPostn(false);
        }
      }).catch(e => {
        debug(e);
      })
  };
  
  const onBtShowLoading = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', true);
  }, []);

  const onBtHide = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', false);
  }, []);

  /*
   * 응답 → 차트/그리드 변환. 측정 이름(SA_AMT/SA_QTY)이 t() 로 번역되어 **차트 데이터의 키**가
   * 되므로, 언어가 바뀌면 이 변환을 통째로 다시 돌려야 한다. 그래서 원본 응답을 ref 에 들고 있다가
   * 아래 useEffect([t]) 에서 재조립한다(재조회 없음).
   */
  const applyTrackingResponse = (responseData, planVersion) => {
    const chartData = [];
    currency.current = responseData.currency;
    const headers = responseData.chartHeader ?? [];
    const getFormatDate = (index) => {
      if (index === dpMeasureType.AMT) {
        return null;
      }
      if (responseData.weekHeader) {
        const weekHeaders = responseData.weekHeader;
        return weekHeaders[headers[index]];
      } else {
        return dateUtils.headerDateFormatter(new ZDate(headers[index]), planVersion[0]?.value[0]?.planConfigObj.BUCKET_UNIT);
      }
    }
    chartName.current = cloneDeep(responseData.chart);
    let versionName = "";
    const symbol = getCurrencySymbol(currency.current) ?? userData?.currencySymbol ?? "";
    for (let idx = 0; idx < headers.length; idx++) {
      let colorFlg = false;
      const cData = {};
      for (let nameIdx = 0; nameIdx < responseData.trackingData.length; nameIdx++) {
        const name = responseData.trackingData[nameIdx].version;
        if (name === dpMeasure.SA_AMT.name || name === dpMeasure.SA_QTY.name) {
          cData[t(name)] = responseData.trackingData[nameIdx][getFormatDate(idx)];
        } else {
          cData[name] = responseData.trackingData[nameIdx][getFormatDate(idx)];
        }
      }
      cData.yyyymmdd = getFormatDate(idx);
      cData.currency = symbol;
      chartData.push(cData);
      for (const row of Object.keys(responseData.chart)) {
        if (getFormatDate(idx) && row === getFormatDate(idx)) {
          versionName = responseData.chart[row];
          colorFlg = true;
        }
      }
      if (!colorFlg && versionName !== "") {
        chartName.current[getFormatDate(idx)] = versionName;
      }
    }
    const versionNameList = [];
    for (let nameIdx = 0; nameIdx < responseData.trackingData.length; nameIdx++) {
      const name = responseData.trackingData[nameIdx].version;
      if (name === dpMeasure.SA_AMT.name || name === dpMeasure.SA_QTY.name) {
        versionNameList.push(t(name))
      } else {
        versionNameList.push(name)
      }
    }
    setTrackingChartDatas(chartData)
    setTrackingDatas(responseData.trackingData);
    setTrackingGridHeader(responseData.header);
    setTrackingChartType(versionNameList);
    setFormatHeader(responseData.header);

    workbenchThreshold.current = responseData?.workbenchThreshold ?? {};
    refData.current = responseData?.referenceData;
  }

  /* 언어 변경 — 조회 없이 마지막 응답으로 차트 데이터/시리즈만 새 언어로 다시 만든다. */
  useEffect(() => {
    const raw = trackingRawRef.current;
    if (isNull(raw) || isUndefined(raw)) {
      return;
    }
    applyTrackingResponse(raw.responseData, raw.planVersion);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const retrieveSearchData = (verList, isPostn, custHrchyCd, itemHrchyCd, allMeasure) => {
    onBtShowLoading();
    const planVersion = verList.filter(plan => isEqual(plan.id, curVersion.current));
    dpTrackingReportService.getTrackingData({
      measureCd: curMeasure.current,
      planVersionList: planVersion[0].value,
      dataType: measureType.current,
      postnCd: isPostn ? custHrchyCd : null,
      customerHrchyCd: !isPostn ? custHrchyCd : null,
      itemHrchyCd,
    })
      .then(response => {
        if (response.data?.message) {
          setSnackInfo({ open: true, severity: response.data.message?.type || snackBarMessageColor.networkError, content: t(response.data.message?.message) || t("MSG_NetworkError") });
        } else {
          if (response?.data) {
            trackingRawRef.current = { responseData: response.data, planVersion };
            applyTrackingResponse(response.data, planVersion);
          }
        }
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, {
          ...reduxViewState, allMeasureList: allMeasure, measureList, measureType: measureType.current, versionList: verList, isPostn,
          selectCd: custHrchyCd, selectItemCd: itemHrchyCd, curMeasure: curMeasure.current, curVersion: curVersion.current, 
          hrchyList: selectList, defaultHrchyValue, defaultItemHrchyValue, filter: filterValue, userRoles// latestDate: latestDate.current
        });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
        onBtHide();
      })
  }

  const convertChartType = (chartType) => {
    const chart = [];
    if (trackingChartType) {
      let count = 0;
      for (const info of trackingChartType) {
        chart.push({
          field: info, text: info, type: chartType, typeOptions: { fiil: colorSet(count), strokeWidth: 3 }, orientation: "left", groupName: t(toggledMeasure),
        });
        count++;
      }
    }
    setChartSeries(chart);
  }

  const cleanDisplay = () => {
    trackingRawRef.current = null; // 지운 뒤 언어를 바꿔도 옛 결과가 되살아나지 않게 한다.
    setTrackingChartDatas([])
    setTrackingDatas([]);
    setMeasureList([]);
  }

  const sortMeasure = (list, type = null) => {
    if (list === null || isEmpty(list)) {
      return [];
    }
    const priorities = {
      MF_AMT: 1, MF_QTY: 1,
      SF_AMT: 2, SF_QTY: 2,
      BF_AMT: 3, BF_QTY: 3,
      FF_AMT: 4, FF_QTY: 4,
      MK_AMT: 5, MK_QTY: 5
    };

    const measureKeys = keys(priorities);
    const paramsType = type ?? measureType.current;
    const filteredList = list.filter(data => data.valueTypeCd === paramsType.toUpperCase() && includes(measureKeys, data.measureCd));
    
    const sortArray = filteredList.sort((a, b) => {
      const getPriority = str => priorities[str] || 0;
      const compareStrings = (str1, str2) => getPriority(str1) - getPriority(str2);
      const aIncludes = measureKeys.find(str => isEqual(str, a.measureCd));
      const bIncludes = measureKeys.find(str => isEqual(str, b.measureCd));
      return compareStrings(aIncludes, bIncludes);
    });

    return sortArray
  }

  const retrieveSearchParameter = () => {
    const planList = [];
    const addPlanversion = [];
    let count = 1;
    dpTrackingReportService.getTrackingSearch()
      .then(response => {
        if (response.data?.message) {
          setSnackInfo({ open: true, severity: response.data.message?.type || snackBarMessageColor.networkError, content: t(response.data.message?.message) || t("MSG_NetworkError") });
          cleanDisplay();
        } else {
          const results = response.data.planVersion;
          let measures = response.data.measure;
          if (results && measures.length > 0) {
            const sortList = sortListByColumn("desc", results, "releaseTs");
            for (const version of sortList) {
              addPlanversion.push(version);
              planList.push({ id: count, value: sortListByColumn("asc", [...addPlanversion], "releaseTs") });
              count++;
            }
            sortListByColumn("asc", measures, "name");

            if (!includes(userRoles, roles.APP_BF)) {
              measures = measures.filter(data => !data.measureCd.includes("BF_"))
              
            }
            // latestDate.current = response.data.latestDate;
            setAllMeasureList(measures)
            setMeasureList(sortMeasure(measures));

            if (!curMeasure.current || reduxViewState.measureType !== measureType.current) {
              curMeasure.current = sortMeasure(measures)?.[0]?.measureCd ?? "";
            }
            
            if (reduxViewState?.curVersion !== curVersion.current) {
              curVersion.current = planList[planList.length - 1].id;
              setVersionList(planList);
            }
            retrieveSearchData(planList, isPostn.current, selectCd.current, selectItemCd.current, measures);

          } else {
            if (measures.length <= 0) {
              setSnackInfo({ open: true, severity: response.data?.message?.type || snackBarMessageColor.inputError, content: t(response.data?.message?.message) || t("MSG_NoActiveMeasure", { dataType: t(measureType.current) }) });
            } else {
              setSnackInfo({ open: true, severity: response.data?.message?.type || snackBarMessageColor.networkErrore, content: t(response.data?.message?.message) || t("MSG_NetworkError") });
            }
            cleanDisplay();
          }
        }
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      });
  };

  const retrievePostn = (postnVal = null) => {
    postnService.getPostnByUser()
      .then(response => {
        if (response.data) {
          if (response.data.postn) {
            if (!reduxViewState?.selectCd) {
              selectCd.current = response.data.postn[0].postnCd;
              setDefaultHrchyValue(response.data.postn[0])
            }
            if (postnVal !== null && reduxViewState && !isEqual(postnVal, reduxViewState?.isPostn)) {
              selectCd.current = response.data.postn[0].postnCd;
              setSelectList(response.data.postn);
            }
            if (!reduxViewState?.hrchyList) {
              setSelectList(response.data.postn);
            }
            isPostn.current = true;
          } else {
            if (!reduxViewState?.selectCd) {
              selectCd.current = response.data.custHrchy[0]?.hrchyCd;
              setDefaultHrchyValue(response.data.custHrchy[0])
            }
            if (postnVal !== null && reduxViewState && !isEqual(postnVal, reduxViewState?.isPostn)) {
              selectCd.current = response.data.custHrchy[0]?.hrchyCd;
              setSelectList(response.data.custHrchy);
            }

            if (!reduxViewState?.hrchyList) {
              setSelectList(response.data.custHrchy);
            }

            if (!reduxViewState?.selectItemCd && response.data?.topItemHrchy) {
              selectItemCd.current = response.data.topItemHrchy?.hrchyCd;
              setDefaultItemHrchyValue(response.data.topItemHrchy)
            }

            isPostn.current = false;
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const getRowStyle = (params) => {
    if (params.node.lastChild) {
      return { borderBottom: "1.5px solid #d9dcde" };
    }
    return null;
  };

  const getRowHeight = () => {
    return 35;
  }

  const handleChangeSelect = (event) => {
    if (versionList.length > 0) {
      if (event.target.name === "versionNumber") {
        curVersion.current = event.target.value;
      } else if (event.target.name === "measure") {
        curMeasure.current = event.target.value
      } else if (event.target.name === "postn") {
        selectCd.current = event.target.value
      }
      retrieveSearchData(versionList, isPostn.current, selectCd.current, selectItemCd.current, allMeasureList);
    }
  };

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userData.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  useEffect(() => {
    if (isReduxPostn !== null) {
      if (reduxViewState && !isEqual(isReduxPostn, reduxViewState?.isPostn)) {
        reduxViewState.selectCd = null;
        retrievePostn(isReduxPostn);
      }
    }
  //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReduxPostn]);

  useEffect(() => {
    if (reduxViewState && !isUndefined(filterValue) && !isEmpty(filterValue) && versionList.length > 0 && (filterValue.customerHrchy?.hrchyCd || filterValue.category?.hrchyCd)) {
      if (filterValue.customerHrchy?.hrchyCd) {
        selectCd.current = filterValue.customerHrchy?.hrchyCd;
      }
      if (filterValue.category?.hrchyCd) {
        selectItemCd.current = filterValue.category.hrchyCd;
      }
      retrieveSearchData(versionList, isPostn.current, selectCd.current, selectItemCd.current, allMeasureList);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  useEffect(() => {
    convertChartType(toggledChart)
    const reduxStates = reduxUtil.createObjectState(viewName, {  ...reduxViewState, chart: toggledChart });
    reduxDispatch(reduxUtil.updateViewState(reduxStates));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggledChart]);

  useEffect(() => {
    measureType.current = toggledMeasure;
    const filterMeasure = allMeasureList ?? reduxViewState?.allMeasureList ?? [];
    if (!isEmpty(filterMeasure)) {
      const sortedMeasures = sortMeasure(filterMeasure, toggledMeasure);
      setMeasureList(sortedMeasures);
      if (reduxViewState?.measureType !== measureType.current) {
        curMeasure.current = sortedMeasures[0].measureCd;
      }
    }
    if (selectList.length > 0) {
      setIsToggle(true);
    }
    const reduxState = reduxUtil.createObjectState(viewName, { measureType: toggledMeasure });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggledMeasure]);

  useEffect(() => {
    if (curMeasure.current !== reduxViewState?.curMeasure && reduxViewState?.versionList) {
      retrieveSearchData(reduxViewState?.versionList, reduxViewState?.isPostn, reduxViewState?.selectCd, reduxViewState?.selectItemCd, reduxViewState?.allMeasureList ?? allMeasureList);
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMeasure.current])

  useEffect(() => {
    if (selectList.length > 0) {
      if (isToggle || !reduxViewState?.curVersion) {
        retrieveSearchParameter();
      } else if (reduxViewState?.versionList?.length > 0) {
        retrieveSearchData(
          reduxViewState.versionList,
          reduxViewState.isPostn,
          reduxViewState.selectCd,
          reduxViewState.selectItemCd,
          reduxViewState.allMeasureList ?? allMeasureList
        );
      }
      setIsToggle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectList]);

  useEffect(() => {
    if (Object.keys(trackingChartType).length > 0) {
      convertChartType(toggledChart)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingChartType])

  useEffect(() => {
    if (!isEmpty(userRoles)) {
      if (reduxViewState) {
        retrievePlanConfig();
      } else {
        retrievePostn();
      }
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  useEffect(() => {
    if (isEmpty(userRoles)) {
      retrieveUserRoles();
    }


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <Box sx={{ position: "relative" }}>
          <FormControl size="small" sx={{ mr: 1.5, width: "200px" }} >
            <InputLabel>{t("versionNumber")}</InputLabel>
            <Select
              id="versionNumber"
              size="small"
              label={t("versionNumber")}
              name="versionNumber"
              value={curVersion.current ? curVersion.current : ""}
              onChange={(event) => { handleChangeSelect(event) }}
            >
              {versionList?.map((version) => {
                return <MenuItem key={version.id} value={version.id}>{version.id}</MenuItem>
              })}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ position: "relative", mr: 1.5 }}>
          <FormControl size="small" sx={{ width: "300px" }} >
            <InputLabel>{t("measure")}</InputLabel>
            <Select
              size="small"
              label={t("measure")}
              name="measure"
              value={curMeasure.current ? curMeasure.current : ""}
              onChange={(event) => { handleChangeSelect(event) }}
            >
              {measureList?.map((measure) => {
                return <MenuItem key={measure.measureCd} value={measure.measureCd}>{t(measure.name)}</MenuItem>
              })}
            </Select>
          </FormControl>
        </Box>
        <FormControl size="small" sx={{ mr: 0.5, minWidth: 200 }} >
          <InputLabel>{isPostn.current ? t("postn") : ""}</InputLabel>
          {isPostn.current ?
            <Select
            size="small"
            name="postn"
            label={t("postn")}
            value={selectCd.current}
            onChange={(event) => { handleChangeSelect(event) }}
            >
              {selectList?.map((postnVal) => {
                return <MenuItem value={postnVal.postnCd} key={postnVal.name}>{postnVal.name}</MenuItem>
              })}
            </Select>
            : <CustomerHrchyTreeFilter viewName={viewName} defaultValue={defaultHrchyValue} enableSelectParent onChangeFilter={setFilterValue} />
          }
        </FormControl>
        {isPostn.current ? null :
          <ItemHrchyTreeFilter
            viewName={viewName}
            onChangeFilter={setFilterValue}
            defaultValue={defaultItemHrchyValue}
            enableSelectParent
          />
        }
        <ToggleButton styles={{ mb: 0.5 }} toggleType="measure" initialValue={toggledMeasure} toggleValue={setToggledMeasure}></ToggleButton>
      </FilterContainer>
      <Box className="grid-area">
        <SplitterContainer initialSizes={[1, 1]}>
          <Box className="ag-theme-balham" sx={{ height: "100%" }}>
            <AgGridReact
              ref={gridRef}
              {...defaultTrackingGridMemo}
              rowData={trackingDatas}
              columnDefs={gridUtils.getColumnDefs(trackingColumnDefs)}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
                singleClickEdit: true,
              }}
              context={{
                workbenchThreshold: workbenchThreshold.current,
                headerList: formatHeader,
                refData: refData.current
              }}
              getRowStyle={getRowStyle}
              getRowHeight={getRowHeight}
              excelStyles={WorkbenchUtil.excelStyles}
              enableBrowserTooltips
              suppressAggFuncInHeader
            />
          </Box>
          <Box className="chart-area">
            <ChartComposed
              xAxis={["yyyymmdd"]}
              dataSeries={chartSeries}
              data={trackingChartDatas}
              tooltipAnimation={false}
              options={{isAmt: isEqual(toggledMeasure.toLowerCase(), AMT) }}
            />
          </Box>
        </SplitterContainer>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
      // onHandler={handleDialog}
      />
    </>
  )
}

export default withTranslation()(DpTrackingReport);
